import React, { useState, useEffect } from 'react';
import { FiArrowLeft, FiArrowRight } from 'react-icons/fi';
import { Progress } from '@/components/custom/progress';
import { ExamsQuestionsModalEnd } from './exams-questions-modal-end';
import { useMutation } from 'jsonapi-react';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { ExamsQuestionsClock } from './exams-questions-clock';
import { useTranslation } from 'react-i18next';
import { DashboardContent } from '@/layouts/dashboard';
import { PageContent, Page, PageSidebar } from '@/components/custom/page';
import { Widget, WidgetBody, WidgetCount, WidgetHeader, WidgetHeaderContent, WidgetTitle } from '@/components/custom/widget';
import { Button } from '@/components/ui/button';
import { QuestionCardAnswer, QuestionCardAnswerLetter, QuestionCardAnswerWrapper, QuestionCardIntroduction, QuestionCardNav, QuestionCardPrint, QuestionCardStatement, QuestionCardTextMotivator, QuestionCardTitle } from '@/components/custom/question';
import { count } from 'draft-js/lib/DefaultDraftBlockRenderMap';
import { cn } from '@/lib/utils';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';
import simuladoIcon from '@/assets/images/infantil/simulado.png';

const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

const RIGHT_WRONG_LETTERS = {
  certo: 'C',
  errado: 'E'
};

export const ExamsQuestionsAlternative = props => {
  const { alternative, question, selectedAlternativeId, setSelectedAlternativeId } = props;
  const playfulStudent = usePlayfulStudent();

  // Nas questões de certo/errado o back-end não preenche o "name" da alternativa,
  // então derivamos a letra (C/E) a partir da descrição para exibir igual às de múltipla escolha.
  const alternativeLetter = question?.modality === 'right_wrong' ? RIGHT_WRONG_LETTERS[alternative?.description?.trim()?.toLowerCase()] ?? alternative.name : alternative.name;

  // Alternativa lúdica: card arredondado com a letra em selo colorido.
  if (playfulStudent) {
    const selected = selectedAlternativeId === alternative.id;
    return (
      <label
        className={cn(
          'flex cursor-pointer items-start gap-3 rounded-[16px] border-2 bg-white p-4 transition-[border-color,background-color] duration-150',
          selected ? 'border-[var(--color-secondary)] bg-[var(--color-bg-lilac)]' : 'border-neutral-100 hover:border-neutral-200'
        )}
      >
        <input
          type="radio"
          className="sr-only"
          name={`question-${question?.id}`}
          id={alternative.id}
          checked={selected}
          onChange={() => {
            setSelectedAlternativeId(alternative.id);
          }}
        />
        <span
          className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-extrabold uppercase', selected ? 'text-white' : 'bg-[var(--color-bg-lilac)] text-[var(--color-secondary)]')}
          style={selected ? { background: 'var(--gradient-btn-start)' } : undefined}
        >
          {alternativeLetter}
        </span>
        <div
          className="min-w-0 flex-1 pt-1 text-sm font-semibold leading-relaxed [&>*:last-child]:mb-0"
          style={{ color: '#222731' }}
          dangerouslySetInnerHTML={{ __html: alternative.description }}
        />
      </label>
    );
  }

  return (
    <QuestionCardAnswer>
      <QuestionCardAnswerLetter>{alternativeLetter}</QuestionCardAnswerLetter>
      <input
        type="radio"
        name={`question-${question?.id}`}
        id={alternative.id}
        checked={selectedAlternativeId === alternative.id}
        onChange={() => {
          setSelectedAlternativeId(alternative.id);
        }}
      />
      <label
        htmlFor={`${alternative?.id}`}
        dangerouslySetInnerHTML={{ __html: alternative.description }}
      />
    </QuestionCardAnswer>
  );
};

export function ExamsQuestionsContent(props) {
  const { t } = useTranslation();
  const { selectQuestionBookTaxonomy, currentQuestion, setCurrentQuestion, questions, examTime, questionBooksUser, updateLastAnswerSubmited, questionBook, taxonomies, activeGroupedQuestions } = props;

  const client = useClient();
  const playfulStudent = usePlayfulStudent();
  const [addAnswer] = useMutation('answers');
  const [selectedAlternativeId, setSelectedAlternativeId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [question, setQuestion] = useState();
  const [activeAnswer, setActiveAnswer] = useState();
  const [elapsedTime, setElapsedTime] = useState(0);
  const [openQuestionAnswer, setOpenQuestionAnswer] = useState('');

  const submitAnswer = async answer => {
    if (!answer) {
      return;
    }

    let response;
    if (!activeAnswer) {
      if (question?.modality === 'discursive') {
        response = await addAnswer({
          content: answer,
          'question-id': question.id,
          'question-books-user-id': questionBooksUser?.id
        });
      } else {
        response = await addAnswer({
          'alternative-id': answer,
          'question-id': question.id,
          'question-books-user-id': questionBooksUser?.id
        });
      }
    } else {
      if (question?.modality === 'discursive') {
        response = await client.mutate(['answers', activeAnswer.id], {
          content: answer,
          'question-id': question.id,
          'question-books-user-id': questionBooksUser?.id
        });
      } else {
        response = await client.mutate(['answers', activeAnswer.id], {
          'alternative-id': answer,
          'question-id': question.id,
          'question-books-user-id': questionBooksUser?.id
        });
      }
    }

    if (response.error) {
      toast.error(response.error.title);
    } else {
      const newAnswer = {
        id: response.data.id,
        alternativeId: response.data['alternative-id'],
        questionId: response.data['question-id'],
        content: response.data.content
      };

      setActiveAnswer(newAnswer);
      updateLastAnswerSubmited(newAnswer);
    }
  };

  const countCorrectAnswers = () => {
    return questionBooksUser.answers.reduce((accumulator, answer) => {
      accumulator += answer.is_correct ? 1 : 0;
      return accumulator;
    }, 0);
  };

  function formatDateTime(dateString) {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes();

    return `${hours}h${minutes}${t('exams.minOfDay')} ${day < 10 ? '0' + day : day}/${month < 10 ? '0' + month : month}/${year}`;
  }

  const formattedDate = formatDateTime(questionBook['finished-at']);

  const handleFinishExam = () => setShowModal(true);

  useEffect(() => {
    if (currentQuestion) {
      setQuestion(questions.find(q => q.id === currentQuestion));
    }
  }, [currentQuestion]);

  useEffect(() => {
    const newActiveAnswer = taxonomies?.[activeGroupedQuestions]?.answers?.find(a => a?.questionId === question?.id);
    setActiveAnswer(newActiveAnswer);
    setSelectedAlternativeId(newActiveAnswer?.alternativeId);
  }, [question, taxonomies]);

  useEffect(() => {
    if (question?.modality === 'discursive') {
      const openQuestionAnswer = questionBooksUser?.answers?.find(a => a?.['question_id'] === currentQuestion)?.content || '';

      if (activeAnswer?.content && activeAnswer?.content !== openQuestionAnswer) {
        setOpenQuestionAnswer(activeAnswer?.content || '');
        return;
      }

      setOpenQuestionAnswer(openQuestionAnswer || '');
    }
  }, [activeAnswer, question]);

  const prevQuestion = () => {
    const questionIndex = combinedArray.findIndex(qId => qId === currentQuestion) - 1;
    const taxonomyIndex = taxonomies.findIndex(t => t.questions.map(q => q.id).includes(combinedArray[questionIndex]));
    setCurrentQuestion(combinedArray[questionIndex]);
    selectQuestionBookTaxonomy(taxonomyIndex);
  };

  const handleNextQuestion = () => {
    const currentIndex = combinedArray.findIndex(qId => qId === question?.id);
    if (currentIndex <= combinedArray.length) {
      const taxonomyIndex = taxonomies.findIndex(t => t.questions.map(q => q.id).includes(combinedArray[currentIndex + 1]));
      setCurrentQuestion(combinedArray[currentIndex + 1]);
      selectQuestionBookTaxonomy(taxonomyIndex);
    }
    setSelectedAlternativeId(null);
    setOpenQuestionAnswer('');
  };

  const getCombinedArray = () => {
    const examQBT = questionBook?.['question-book-taxonomies'];
    if (!examQBT) {
      return [];
    }
    return examQBT.reduce((accumulator, taxonomy) => {
      return accumulator.concat(taxonomy.question_ids);
    }, []);
  };
  const combinedArray = getCombinedArray();

  function countAnswered() {
    return questionBook.taxonomies.reduce((accumulator, question) => {
      accumulator += question.answers.length;
      return accumulator;
    }, 0);
  }

  function sumQuestionIds(exam) {
    const taxonomyArray = exam['question-book-taxonomies']?.map(taxonomy => taxonomy.question_ids) ?? [];
    return taxonomyArray.reduce((accumulator, current) => accumulator + current.length, 0);
  }

  const isLastQuestion = currentQuestion === combinedArray[combinedArray.length - 1];
  const primaryDisabled = !isLastQuestion && (question?.modality === 'discursive' ? openQuestionAnswer.length === 0 : !selectedAlternativeId);

  const handlePrimary = () => {
    submitAnswer(question?.modality === 'discursive' ? openQuestionAnswer : selectedAlternativeId);
    if (isLastQuestion) {
      setTimeout(handleFinishExam, 1000);
    } else {
      handleNextQuestion();
    }
  };

  const questionStatementBlock = (
    <>
      {question?.['motivator-texts']?.length > 0 ? (
        question['motivator-texts'].map((motivatorText, index) => (
          <QuestionCardTextMotivator key={index}>
            <QuestionCardTitle dangerouslySetInnerHTML={{ __html: motivatorText.title }} />
            <div dangerouslySetInnerHTML={{ __html: motivatorText.body }} />
            <br />
            <QuestionCardPrint dangerouslySetInnerHTML={{ __html: motivatorText.print }} />
          </QuestionCardTextMotivator>
        ))
      ) : question?.introduction?.body ? (
        <QuestionCardTextMotivator>
          <div dangerouslySetInnerHTML={{ __html: question.introduction.body }} />
        </QuestionCardTextMotivator>
      ) : null}

      <QuestionCardStatement dangerouslySetInnerHTML={{ __html: question?.description }} />
    </>
  );

  const discursiveTextarea = (
    <textarea
      placeholder={t('exams.discursiveQuestionPlaceholder')}
      value={openQuestionAnswer}
      onChange={e => setOpenQuestionAnswer(e.target.value)}
      className={cn('mb-6 mt-4 h-32 w-full p-2', playfulStudent ? 'rounded-[16px] border-2 border-neutral-100 p-4 text-sm font-semibold focus:border-[var(--color-secondary)] focus:outline-none' : 'rounded border border-gray-300')}
    />
  );

  const alternativesList = question?.alternatives?.map(alternative => (
    <ExamsQuestionsAlternative
      key={alternative.id}
      submitAnswer={submitAnswer}
      selectedAlternativeId={selectedAlternativeId}
      setSelectedAlternativeId={setSelectedAlternativeId}
      question={question}
      alternative={alternative}
      activeAnswer={activeAnswer}
    />
  ));

  if (playfulStudent) {
    const answeredCount = countAnswered();
    const totalCount = sumQuestionIds(questionBook);
    const answeredPct = totalCount ? Math.floor((answeredCount / totalCount) * 100) : 0;

    return (
      <DashboardContent
        viewMode="create"
        className="pt-6 md:pt-8"
      >
        {/* Cabeçalho: ilustração, título, tempo e entrega. */}
        <div
          className="mb-5 flex flex-wrap items-center gap-4 rounded-[24px] p-5 shadow-card"
          style={{ background: 'var(--color-bg-lilac)', animation: `trilha-in 380ms ${EASE_OUT} both` }}
        >
          <img
            src={simuladoIcon}
            alt=""
            aria-hidden="true"
            className="h-14 w-14 shrink-0 object-contain drop-shadow-sm"
          />
          <h1
            className="min-w-[220px] flex-1 text-lg font-extrabold leading-snug"
            style={{ color: '#222731' }}
          >
            {questionBook?.title}
          </h1>

          <ExamsQuestionsClock
            playful
            examTime={examTime}
            questionBooksUser={questionBooksUser}
            questionBook={questionBook}
            setElapsedTime={setElapsedTime}
            elapsedTime={elapsedTime}
          />

          {currentQuestion > 3 && (
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="rounded-full border-2 border-[var(--color-mission-medium)] bg-white px-5 py-2 text-sm font-extrabold transition-transform duration-100 active:scale-[0.97]"
              style={{ color: 'var(--color-mission-text)' }}
            >
              {t('exams.sendExam')}
            </button>
          )}
        </div>

        <div className="grid items-start gap-5 lg:grid-cols-[1fr_300px]">
          {/* Card da questão. */}
          <div
            className="rounded-[24px] bg-white p-6 shadow-card md:p-8"
            style={{ animation: `trilha-in 380ms ${EASE_OUT} 150ms both` }}
          >
            <span
              className="inline-block rounded-full bg-[var(--color-bg-mission-soft)] px-3 py-1 text-xs font-extrabold"
              style={{ color: 'var(--color-mission-text)' }}
            >
              {t('exams.questionTitle')} {combinedArray.indexOf(currentQuestion) + 1} de {totalCount}
            </span>

            <div className="mt-4">{questionStatementBlock}</div>

            {question?.modality === 'discursive' ? discursiveTextarea : <div className="mt-5 flex flex-col gap-3">{alternativesList}</div>}

            <div className="mt-7 flex flex-wrap items-center justify-between gap-3">
              <div>
                {currentQuestion !== combinedArray[0] && (
                  <button
                    type="button"
                    onClick={prevQuestion}
                    className="flex items-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-bold text-neutral-500 transition-colors duration-150 hover:bg-neutral-50 hover:text-neutral-700"
                  >
                    <FiArrowLeft className="h-4 w-4" /> {t('exams.previous')}
                  </button>
                )}
              </div>

              <div className="flex items-center gap-2">
                {!isLastQuestion && (
                  <button
                    type="button"
                    onClick={handleNextQuestion}
                    className="flex items-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-bold text-neutral-500 transition-colors duration-150 hover:bg-neutral-50 hover:text-neutral-700"
                  >
                    {t('exams.next')} <FiArrowRight className="h-4 w-4" />
                  </button>
                )}

                <button
                  type="button"
                  disabled={primaryDisabled}
                  onClick={handlePrimary}
                  className={cn(
                    'relative overflow-hidden rounded-full px-8 py-3 text-sm font-extrabold transition-transform duration-100',
                    primaryDisabled ? 'cursor-not-allowed border border-neutral-200 bg-neutral-100 text-neutral-400' : 'text-white active:scale-[0.97]'
                  )}
                  style={primaryDisabled ? undefined : { background: 'var(--gradient-btn-continue)', boxShadow: '0 4px 0 var(--color-mission-dark), 0 6px 12px rgba(245,123,46,0.35)' }}
                >
                  {isLastQuestion ? t('exams.replyAndFinish') : t('exams.reply')}
                </button>
              </div>
            </div>
          </div>

          {/* Lateral: progresso + numeração das questões. */}
          <aside
            className="flex flex-col gap-5 lg:sticky lg:top-6"
            style={{ animation: `trilha-in 380ms ${EASE_OUT} 100ms both` }}
          >
            <div className="rounded-[24px] bg-white p-5 shadow-card">
              <div className="mb-2 flex items-baseline justify-between gap-2">
                <span
                  className="text-sm font-extrabold"
                  style={{ color: '#222731' }}
                >
                  Progresso
                </span>
                <span className="whitespace-nowrap text-xs font-bold text-neutral-500">
                  {answeredCount}/{totalCount} respondidas
                </span>
              </div>
              <div className="h-2.5 w-full overflow-hidden rounded-full bg-[var(--color-bg-lilac)]">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${Math.min(answeredPct, 100)}%`, background: 'var(--gradient-progress-bar)' }}
                />
              </div>
            </div>

            <div className="rounded-[24px] bg-white p-5 shadow-card">
              <span
                className="mb-3 block text-sm font-extrabold"
                style={{ color: '#222731' }}
              >
                Questões
              </span>
              {/* Numeração contínua, sem códigos de descritor (D1, D2…): o
                  aluno não precisa ver a taxonomia oficial. */}
              <div className="flex flex-wrap gap-2">
                {questionBook?.['question-book-taxonomies']?.map((qbTaxonomy, i) =>
                  qbTaxonomy.question_ids.map(questionId => {
                    const isActive = currentQuestion === questionId;
                    const isAnswered = !!questionBook.taxonomies[i].answers.find(q => q.questionId === questionId);
                    const number = combinedArray.indexOf(questionId) + 1;

                    return (
                      <button
                        key={questionId}
                        type="button"
                        disabled={isAnswered}
                        onClick={() => {
                          setCurrentQuestion(questionId);
                          selectQuestionBookTaxonomy(i);
                        }}
                        className={cn(
                          'flex h-9 w-9 items-center justify-center rounded-full text-xs font-extrabold transition-transform duration-100',
                          isActive ? 'text-white' : isAnswered ? 'cursor-default bg-success-light text-success' : 'bg-[var(--color-bg-lilac)] text-[var(--color-secondary)] active:scale-[0.95]'
                        )}
                        style={isActive ? { background: 'var(--gradient-btn-start)' } : undefined}
                      >
                        {number < 10 ? `0${number}` : number}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </aside>
        </div>

        <ExamsQuestionsModalEnd
          questionBooksUser={questionBooksUser}
          countCorrectAnswers={countCorrectAnswers}
          elapsedTime={elapsedTime}
          countAnswered={countAnswered}
          show={showModal}
          onClose={() => {
            setShowModal(false);
          }}
          formattedDate={formattedDate}
          questionBook={questionBook}
          questions={questions}
        />
      </DashboardContent>
    );
  }

  return (
    <DashboardContent viewMode="create">
      <Page>
        <PageContent bg>
          <div>
            <QuestionCardIntroduction>
              {t('exams.questionTitle')} {combinedArray.indexOf(currentQuestion) + 1}
            </QuestionCardIntroduction>

            {questionStatementBlock}

            {question?.modality === 'discursive' ? discursiveTextarea : <QuestionCardAnswerWrapper>{alternativesList}</QuestionCardAnswerWrapper>}

            {isLastQuestion ? (
              <>
                <Button
                  wide
                  type="button"
                  onClick={handlePrimary}
                >
                  {t('exams.replyAndFinish')}
                </Button>

                <QuestionCardNav>
                  {currentQuestion !== combinedArray[0] && (
                    <Button
                      variant="none"
                      wide
                      type="button"
                      onClick={prevQuestion}
                    >
                      <FiArrowLeft /> {t('exams.previous')}
                    </Button>
                  )}
                </QuestionCardNav>
              </>
            ) : (
              <>
                <Button
                  wide
                  type="button"
                  disabled={primaryDisabled}
                  onClick={handlePrimary}
                >
                  {t('exams.reply')}
                </Button>

                <QuestionCardNav>
                  {currentQuestion !== combinedArray[0] && (
                    <Button
                      variant="none"
                      wide
                      type="button"
                      onClick={prevQuestion}
                    >
                      <FiArrowLeft /> {t('exams.previous')}
                    </Button>
                  )}

                  {currentQuestion !== combinedArray[combinedArray.length - 1] && (
                    <Button
                      variant="none"
                      wide
                      type="button"
                      onClick={handleNextQuestion}
                    >
                      {t('exams.next')} <FiArrowRight />
                    </Button>
                  )}
                </QuestionCardNav>
              </>
            )}
          </div>
        </PageContent>

        <PageSidebar>
          {currentQuestion > 3 ? (
            <Button
              variant="outline"
              fullWidth
              type="button"
              className="mb-12"
              onClick={() => {
                if (currentQuestion > 3) {
                  // stopTimer();
                  setShowModal(true);
                }
              }}
            >
              {t('exams.sendExam')}
            </Button>
          ) : (
            ''
          )}

          <ExamsQuestionsClock
            examTime={examTime}
            questionBooksUser={questionBooksUser}
            questionBook={questionBook}
            setElapsedTime={setElapsedTime}
            elapsedTime={elapsedTime}
          />

          <Widget>
            <WidgetHeader>
              <WidgetHeaderContent>
                <WidgetTitle>{t('textsCommon.questions')}</WidgetTitle>
                <WidgetCount>
                  <span>
                    {countAnswered()}/{sumQuestionIds(questionBook)}
                  </span>
                </WidgetCount>
              </WidgetHeaderContent>
            </WidgetHeader>

            <WidgetBody>
              <Progress valueNow={Math.floor((countAnswered() / sumQuestionIds(questionBook)) * 100)} />
            </WidgetBody>
          </Widget>
        </PageSidebar>
      </Page>

      <ExamsQuestionsModalEnd
        questionBooksUser={questionBooksUser}
        countCorrectAnswers={countCorrectAnswers}
        elapsedTime={elapsedTime}
        countAnswered={countAnswered}
        show={showModal}
        onClose={() => {
          setShowModal(false);
        }}
        formattedDate={formattedDate}
        questionBook={questionBook}
        questions={questions}
      />
    </DashboardContent>
  );
}
